import { type CSSProperties, useMemo, useState } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { NavIcon, useBrandTokens } from '../../shell/shared';
import { SUGGESTED_TAGS } from './queue-data';
import { usePowerSyncQuery } from '../../adapters/powersync';
import {
  qAppointmentProviders,
  qAppointmentServices,
  type AppointmentProviderRow,
  type AppointmentServiceRow,
  filterBookableServices,
} from '@helios/data-access';
import { useLaboratorySettings } from '../../hooks/useLaboratory';

// ─── Types ───

export interface AddToQueuePrefill {
  patientId: string;
  patientName: string;
  appointmentId?: string;
  providerId?: string;
  serviceId?: string;
  notes?: string;
}

export interface AddToQueueData {
  patientId: string;
  patientName: string;
  appointmentId?: string;
  providerId: string;
  serviceId: string;
  priority: string;
  notes: string;
  tags: string[];
  arrived: boolean;
}

interface AddToQueueDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefill: AddToQueuePrefill | null;
  onAdd: (data: AddToQueueData) => void;
}

// ─── Component ───

export function AddToQueueDrawer({ open, onOpenChange, prefill, onAdd }: AddToQueueDrawerProps) {
  const brandTokens = useBrandTokens();

  const [providerId, setProviderId] = useState('');
  const [serviceId, setServiceId] = useState('');
  const [priority, setPriority] = useState('normal');
  const [notes, setNotes] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [arrived, setArrived] = useState(false); // Appointments default to not arrived

  // Provider/service dropdowns
  const { data: providerRows = [] } =
    usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: serviceRows = [] } =
    usePowerSyncQuery<AppointmentServiceRow>(qAppointmentServices());

  const activeProviders = useMemo(() => providerRows.filter((p) => p.active === 1), [providerRows]);
  // PRD.329 follow-up — optionally hide lab tests (Laboratory Settings, default off).
  const { hideLabTestsFromBooking } = useLaboratorySettings();
  const activeServices = useMemo(
    () =>
      filterBookableServices(
        serviceRows.filter((s) => s.active === 1),
        hideLabTestsFromBooking,
        [prefill?.serviceId],
      ),
    [serviceRows, hideLabTestsFromBooking, prefill?.serviceId],
  );

  // Reset form when prefill changes (drawer opens with new data)
  const [lastPrefillId, setLastPrefillId] = useState<string | null>(null);
  if (prefill && prefill.appointmentId !== lastPrefillId) {
    setLastPrefillId(prefill.appointmentId ?? null);
    setProviderId(prefill.providerId ?? '');
    setServiceId(prefill.serviceId ?? '');
    setPriority('normal');
    setNotes(prefill.notes ?? '');
    setTags([]);
    setTagInput('');
    setArrived(false);
  }

  const resetForm = () => {
    setProviderId('');
    setServiceId('');
    setPriority('normal');
    setNotes('');
    setTags([]);
    setTagInput('');
    setArrived(false);
    setLastPrefillId(null);
  };

  const handleClose = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  const handleAdd = () => {
    if (!prefill) return;
    onAdd({
      patientId: prefill.patientId,
      patientName: prefill.patientName,
      appointmentId: prefill.appointmentId,
      providerId,
      serviceId,
      priority,
      notes,
      tags,
      arrived,
    });
    resetForm();
  };

  const toggleTag = (tag: string) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      if (!tags.includes(tagInput.trim())) {
        setTags((prev) => [...prev, tagInput.trim()]);
      }
      setTagInput('');
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={handleClose}
      title="Add to Queue"
      width={440}
      portalStyle={brandTokens as CSSProperties}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button variant="outline" size="lg" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button variant="gradient" size="lg" onClick={handleAdd}>
            Add to Queue
          </Button>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--sp-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-5)',
        }}
      >
        {/* Patient (locked) */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={labelStyle}>Patient</label>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              padding: 'var(--sp-3)',
              border: '1px solid var(--color-primary)',
              borderRadius: 'var(--input-radius, 10px)',
              background: 'var(--bg-primary-subtle)',
            }}
          >
            <NavIcon name="user" />
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
              }}
            >
              {prefill?.patientName ?? 'Unknown'}
            </span>
          </div>
        </div>

        {/* Queue Details */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Queue Details
          </span>

          {/* Provider */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Provider</label>
            <select
              value={providerId}
              onChange={(e) => setProviderId(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select provider...</option>
              {activeProviders.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.display_name ?? p.full_name}
                </option>
              ))}
            </select>
          </div>

          {/* Service */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Service</label>
            <select
              value={serviceId}
              onChange={(e) => setServiceId(e.target.value)}
              style={inputStyle}
            >
              <option value="">Select service...</option>
              {activeServices.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={labelStyle}>Priority</label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              style={inputStyle}
            >
              <option value="normal">Normal</option>
              <option value="urgent">Urgent</option>
              <option value="vip">VIP</option>
            </select>
          </div>
        </div>

        {/* Patient arrived? */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--sp-3) var(--sp-4)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--input-radius, 10px)',
            background: 'var(--bg-surface)',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--text)',
              }}
            >
              Patient arrived?
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                marginTop: 2,
              }}
            >
              {arrived
                ? 'Patient is here — will be added as Waiting.'
                : "Not yet here — will be added as Expected and won't affect wait times."}
            </div>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={arrived}
            onClick={() => setArrived(!arrived)}
            style={{
              width: 36,
              height: 20,
              borderRadius: 'var(--radius-full)',
              border: 'none',
              background: arrived
                ? 'var(--color-primary, #2563eb)'
                : 'var(--bg-surface-2, #e2e8f0)',
              position: 'relative',
              cursor: 'pointer',
              transition: 'background 0.2s',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 2,
                left: arrived ? 18 : 2,
                width: 16,
                height: 16,
                borderRadius: 'var(--radius-full)',
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                transition: 'left 0.2s',
              }}
            />
          </button>
        </div>

        {/* Notes */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={labelStyle}>
            Notes{' '}
            <span
              style={{
                color: 'var(--text-muted)',
                fontWeight: 'var(--font-weight-normal)',
                fontSize: 'var(--font-size-xs)',
              }}
            >
              (optional)
            </span>
          </label>
          <textarea
            placeholder="Add any notes or special instructions..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{
              ...inputStyle,
              height: 'auto',
              padding: 'var(--sp-3)',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </div>

        {/* Tags */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={labelStyle}>
            Tags{' '}
            <span
              style={{
                color: 'var(--text-muted)',
                fontWeight: 'var(--font-weight-normal)',
                fontSize: 'var(--font-size-xs)',
              }}
            >
              (optional)
            </span>
          </label>
          <div style={{ position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                left: 12,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-muted)',
                display: 'flex',
                pointerEvents: 'none',
              }}
            >
              <NavIcon name="tag" />
            </div>
            <input
              type="text"
              placeholder="Type a tag and press Enter..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagKeyDown}
              style={{ ...inputStyle, paddingLeft: 40 }}
            />
          </div>

          {/* Selected tags */}
          {tags.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {tags.map((tag) => (
                <span
                  key={tag}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '2px 8px',
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--bg-primary-subtle)',
                    color: 'var(--color-primary)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-medium)',
                  }}
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => toggleTag(tag)}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: 'inherit',
                      cursor: 'pointer',
                      padding: 0,
                      fontSize: 12,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Suggested */}
          <div>
            <span
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
                fontWeight: 'var(--font-weight-medium)',
              }}
            >
              Suggested:
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {SUGGESTED_TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  style={{
                    padding: '2px 10px',
                    borderRadius: 'var(--radius-full)',
                    border: '1px solid var(--border)',
                    background: tags.includes(tag)
                      ? 'var(--bg-primary-subtle)'
                      : 'var(--bg-surface)',
                    color: tags.includes(tag) ? 'var(--color-primary)' : 'var(--text-muted)',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-medium)',
                    cursor: 'pointer',
                  }}
                >
                  + {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  );
}

// ─── Styles ───

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--text)',
};

const inputStyle: CSSProperties = {
  width: '100%',
  height: 'var(--input-height, 40px)',
  padding: '0 var(--input-padding-x, 14px)',
  borderRadius: 'var(--input-radius, 10px)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  fontFamily: 'inherit',
  boxSizing: 'border-box' as const,
};
